import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useOptimizedHotel } from '@/contexts/OptimizedHotelContext';
import { useSelectiveSubscription } from '@/hooks/useSelectiveSubscription';
import { Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';

type RoomCategory = Tables<'room_categories'>;
type RoomCategoryInsert = TablesInsert<'room_categories'>;
type RoomCategoryUpdate = TablesUpdate<'room_categories'>;

export const useRoomCategories = () => {
  const [categories, setCategories] = useState<RoomCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { currentHotel } = useOptimizedHotel();

  const fetchCategories = async () => {
    try {
      setLoading(true);
      
      if (!currentHotel) {
        console.log('🏨 useRoomCategories: No current hotel available');
        setCategories([]);
        setLoading(false);
        return;
      }

      // Set hotel context for RLS policies
      await supabase.rpc('set_current_hotel_context', {
        hotel_uuid: currentHotel.id
      });

      // Explicit filtering by hotel_id + RLS as backup
      const { data, error } = await supabase
        .from('room_categories')
        .select('*')
        .eq('hotel_id', currentHotel.id)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });

      if (error) throw error;
      setCategories(data || []);
      console.log('🏨 useRoomCategories: Loaded', data?.length || 0, 'categories for hotel:', currentHotel.name);
    } catch (error: any) {
      console.error('Error fetching room categories:', error);
      toast({
        title: "Ошибка",
        description: "Не удалось загрузить категории номеров",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const createCategory = async (category: Omit<RoomCategoryInsert, 'hotel_id' | 'id' | 'created_at' | 'updated_at'>) => {
    try {
      if (!currentHotel) throw new Error('No current hotel selected');

      const { data, error } = await supabase
        .from('room_categories')
        .insert({ ...category, hotel_id: currentHotel.id })
        .select()
        .single();

      if (error) throw error;

      setCategories(prev => [data, ...prev]);
      toast({
        title: "Успешно",
        description: "Категория номеров создана",
      });

      return data;
    } catch (error: any) {
      console.error('Error creating room category:', error);
      toast({
        title: "Ошибка",
        description: error.message || "Не удалось создать категорию номеров",
        variant: "destructive",
      });
      throw error;
    }
  };

  const createCategoryWithRooms = async (categoryData: any) => {
    try {
      if (!currentHotel) throw new Error('No current hotel selected');

      // Extract room generation parameters
      const { create_rooms, rooms_count, room_prefix, start_number, floor_number, ...categoryInfo } = categoryData;

      // Create category first
      const { data: newCategory, error: categoryError } = await supabase
        .from('room_categories')
        .insert({ ...categoryInfo, hotel_id: currentHotel.id })
        .select()
        .single();

      if (categoryError) throw categoryError;

      // If auto-generation is enabled, create rooms
      if (create_rooms && rooms_count > 0) {
        const roomsToCreate = [];
        
        for (let i = 0; i < rooms_count; i++) {
          const roomNumber = start_number + i;
          const prefix = room_prefix ? `${room_prefix} ` : '';
          const roomName = `${prefix}${categoryInfo.name} ${roomNumber}`;
          
          // Generate room_number based on floor if provided
          let actualRoomNumber = roomNumber.toString();
          if (floor_number) {
            actualRoomNumber = `${floor_number}${(i + 1).toString().padStart(2, '0')}`;
          }
          
          roomsToCreate.push({
            name: roomName,
            room_number: actualRoomNumber,
            room_type: categoryInfo.name,
            room_category_id: newCategory.id,
            hotel_id: currentHotel.id,
            capacity: categoryInfo.base_capacity,
            // 🎯 price_per_night УСТАРЕЛ - цены через сезонную архитектуру
            bed_type: categoryInfo.bed_type,
            view_type: categoryInfo.view_type,
            bathroom_type: categoryInfo.bathroom_type,
            amenities: typeof categoryInfo.amenities === 'string' 
              ? categoryInfo.amenities.split(',').map((item: string) => item.trim()).filter(Boolean)
              : categoryInfo.amenities || [],
            is_available: true,
            is_published: false,
            floor_number: floor_number ? parseInt(floor_number) : null,
            description: `${categoryInfo.name} номер с базовой комплектацией`,
          });
        }

        // Create all rooms
        const { error: roomsError } = await supabase
          .from('rooms')
          .insert(roomsToCreate);

        if (roomsError) {
          console.error('Error creating rooms:', roomsError);
          // Don't throw here, category is already created
          toast({
            title: "Частичная ошибка",
            description: `Категория создана, но не удалось создать номера: ${roomsError.message}`,
            variant: "destructive",
          });
        } else {
          toast({
            title: "Успешно",
            description: `Категория создана и добавлено ${rooms_count} номеров`,
          });
        }
      } else {
        toast({
          title: "Успешно",
          description: "Категория номеров создана",
        });
      }

      setCategories(prev => [newCategory, ...prev]);
      return newCategory;
    } catch (error: any) {
      console.error('Error creating category with rooms:', error);
      toast({
        title: "Ошибка",
        description: error.message || "Не удалось создать категорию номеров",
        variant: "destructive",
      });
      throw error;
    }
  };

  const updateCategory = async (id: string, updates: RoomCategoryUpdate) => {
    try {
      const { data, error } = await supabase
        .from('room_categories')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      setCategories(prev => prev.map(category => 
        category.id === id ? data : category
      ));

      toast({
        title: "Успешно",
        description: "Категория номеров обновлена",
      });

      return data;
    } catch (error: any) {
      console.error('Error updating room category:', error);
      toast({
        title: "Ошибка",
        description: error.message || "Не удалось обновить категорию номеров",
        variant: "destructive",
      });
      throw error;
    }
  };

  const deleteCategory = async (id: string) => {
    try {
      const { error } = await supabase
        .rpc('delete_room_category_safe', { p_category_id: id });

      if (error) throw error;

      setCategories(prev => prev.filter(category => category.id !== id));
      toast({
        title: "Успешно",
        description: "Категория номеров удалена",
      });
    } catch (error: any) {
      console.error('Error deleting room category:', error);
      toast({
        title: "Ошибка",
        description: error.message || "Не удалось удалить категорию номеров",
        variant: "destructive",
      });
      throw error;
    }
  };

  const toggleCategoryActive = async (id: string) => {
    const category = categories.find(c => c.id === id);
    if (!category) return;

    await updateCategory(id, { is_active: !category.is_active });
  };

  // Optimized realtime subscription
  useSelectiveSubscription(
    {
      table: 'room_categories',
      event: '*',
      filter: currentHotel ? `hotel_id=eq.${currentHotel.id}` : undefined
    },
    () => {
      if (currentHotel) {
        console.log('🏨 useRoomCategories: Real-time update - refetching categories');
        fetchCategories();
      }
    },
    {
      enabled: !!currentHotel,
      debounceMs: 1000,
      priority: 'medium',
      onlyWhenVisible: true,
      onError: (error) => {
        console.error('Realtime subscription error for room categories:', error);
        toast({
          title: "Проблема с обновлением",
          description: "Данные о категориях номеров могут быть устаревшими",
          variant: "destructive",
        });
      }
    }
  );

  useEffect(() => {
    // Only fetch categories if we have a current hotel
    if (currentHotel) {
      console.log('🏨 useRoomCategories: Fetching categories for hotel:', currentHotel.name, currentHotel.id);
      fetchCategories();
    } else {
      console.log('🏨 useRoomCategories: No current hotel, clearing categories');
      setCategories([]);
      setLoading(false);
    }
  }, [currentHotel?.id]); // Only depend on hotel ID, not entire object

  return {
    categories,
    loading,
    createCategory,
    createCategoryWithRooms,
    updateCategory,
    deleteCategory,
    toggleCategoryActive,
    refetch: fetchCategories,
  };
};